import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="catalog--bulk-operations"
// Manages checkbox multi-select with bulk action bar for items list
// Actions: status change, category assign, delete
//
// CONEK-6290: this controller sits on the page layout, OUTSIDE the
// items-table Turbo frame that paginates. A page change swaps the rows but
// keeps this controller, so the selection lives here (not in the checkboxes)
// and is kept across pages; rows coming back into view are re-ticked. A filter
// change is a full visit, which reconnects the controller and starts afresh —
// a selection never outlives the filter it was made under.
//
// "Select all N matching the filter" (mirroring the Branch Items matrix) sets
// select_all_matching=true on every bulk form and sends no ids: the server
// re-derives the set from the list's filter.
export default class extends Controller {
  static targets = [
    "selectAll",
    "checkbox",
    "actionBar",
    "count",
    "statusForm",
    "categoryForm",
    "destroyForm",
    "idsContainer",
    "statusIdsContainer",
    "categoryIdsContainer",
    "destroyIdsContainer",
    "returnQuery",
    "selectAllMatching", // hidden select_all_matching flag in each bulk form
    "selectAllMatchingToggle", // "Select all N matching the filter"
    "matchingNote", // "All N matching the filter selected."
    "offPageNote", // "(M on other pages)"
  ]

  static values = {
    selectedText: { type: String, default: "%{count} selected" },
    offPageText: { type: String, default: "(%{count} on other pages)" },
    totalMatching: { type: Number, default: 0 }, // items matching the filter
  }

  connect() {
    this.selection = new Set()
    this.matchingMode = false
    this.updateState()
  }

  // A page of rows swapped in by the frame: restore each row from the kept
  // selection. Batched, so a 50-row page costs one state refresh, not 100.
  checkboxTargetConnected(checkbox) {
    if (!this.selection) return
    checkbox.checked = this.matchingMode || this.selection.has(checkbox.value)
    checkbox.disabled = this.matchingMode
    this.scheduleUpdate()
  }

  // Rows leaving the page stay selected: only the counter's split changes.
  checkboxTargetDisconnected() {
    if (!this.selection) return
    this.scheduleUpdate()
  }

  scheduleUpdate() {
    if (this.updateScheduled) return
    this.updateScheduled = true
    queueMicrotask(() => {
      this.updateScheduled = false
      if (this.selection) this.updateState()
    })
  }

  toggle(event) {
    const source = event.target
    this.syncSiblingCheckboxes(source)
    if (source.checked) {
      this.selection.add(source.value)
    } else {
      this.selection.delete(source.value)
    }
    this.updateState()
  }

  // Propagate a checkbox's checked state to any other checkbox for the same
  // logical row (same value), so desktop and mobile stay in lockstep and an
  // unticked row is really dropped from the selection. (CONEK-6287)
  syncSiblingCheckboxes(source) {
    this.checkboxTargets.forEach((cb) => {
      if (cb !== source && cb.value === source.value) {
        cb.checked = source.checked
      }
    })
  }

  toggleAll() {
    const checked = this.selectAllTarget.checked
    this.checkboxTargets.forEach((cb) => {
      cb.checked = checked
      if (checked) {
        this.selection.add(cb.value)
      } else {
        this.selection.delete(cb.value)
      }
    })
    this.updateState()
  }

  // "Select all N matching the filter" — act on the whole filtered set
  // server-side. The page boxes are ticked and locked so they can't be
  // toggled into a confusing partial state. (Same as the matrix.)
  selectAllMatching(event) {
    if (event) event.preventDefault()
    this.matchingMode = true
    this.checkboxTargets.forEach((cb) => {
      cb.checked = true
      cb.disabled = true
    })
    this.updateState()
  }

  // Clear drops everything: all-matching mode and rows kept from other pages.
  clearSelection(event) {
    if (event) event.preventDefault()
    this.matchingMode = false
    this.selection.clear()
    this.checkboxTargets.forEach((cb) => {
      cb.checked = false
      cb.disabled = false
    })
    this.updateState()
  }

  updateState() {
    const pageValues = new Set(this.checkboxTargets.map((cb) => cb.value))
    const selectedOnPage = [...pageValues].filter((v) =>
      this.selection.has(v),
    ).length
    const offPage = this.selection.size - selectedOnPage
    const count = this.matchingMode
      ? this.totalMatchingValue
      : this.selection.size
    const pageFull = pageValues.size > 0 && selectedOnPage === pageValues.size

    if (this.hasSelectAllTarget) {
      this.selectAllTarget.disabled = this.matchingMode
      this.selectAllTarget.checked = this.matchingMode || pageFull
      this.selectAllTarget.indeterminate =
        !this.matchingMode && selectedOnPage > 0 && !pageFull
    }

    // Show/hide action bar
    if (count > 0) {
      this.actionBarTarget.classList.remove("hidden")
      this.countTarget.textContent = this.selectedTextValue.replace(
        "%{count}",
        count,
      )
    } else {
      this.actionBarTarget.classList.add("hidden")
    }

    // Say plainly that part of the selection is on other pages, so a kept
    // selection is never a surprise when a bulk action runs.
    if (this.hasOffPageNoteTarget) {
      const showOffPage = !this.matchingMode && offPage > 0
      this.offPageNoteTarget.textContent = showOffPage
        ? this.offPageTextValue.replace("%{count}", offPage)
        : ""
      this.offPageNoteTarget.classList.toggle("hidden", !showOffPage)
    }
    if (this.hasMatchingNoteTarget) {
      this.matchingNoteTarget.classList.toggle("hidden", !this.matchingMode)
    }
    // Offer "select all matching" only once the page is fully selected and
    // the filter matches more than is already selected.
    if (this.hasSelectAllMatchingToggleTarget) {
      const moreToSelect = this.totalMatchingValue > this.selection.size
      this.selectAllMatchingToggleTarget.classList.toggle(
        "hidden",
        this.matchingMode || !(pageFull && moreToSelect),
      )
    }

    this.selectAllMatchingTargets.forEach((input) => {
      input.value = this.matchingMode ? "true" : "false"
    })

    // Sync hidden inputs for all bulk action forms. In all-matching mode no
    // ids travel: the server re-derives them from the filter.
    const ids = this.matchingMode ? [] : this.selectedValues()
    this.syncIds(this.statusIdsContainerTarget, ids, "ids[]")
    this.syncIds(this.categoryIdsContainerTarget, ids, "ids[]")
    this.syncIds(this.destroyIdsContainerTarget, ids, "ids[]")

    this.syncReturnQuery()
  }

  // CONEK-6290: the items-table frame pushes page/filter changes into the
  // URL without re-rendering this bar, so refresh the list query each bulk
  // form carries — the action then returns to the list the user is on.
  //
  // CONEK-9030: the frame's ui--url-sync pushes the new URL on
  // turbo:frame-load, which Turbo fires AFTER the new rows connected — so the
  // refresh their connect schedules still sees the previous page. This is
  // therefore also bound to turbo:frame-load (it bubbles here after url-sync's
  // own listener on the frame ran) and to each bulk form's submit, which reads
  // the URL right before the action leaves the page.
  syncReturnQuery() {
    this.returnQueryTargets.forEach((input) => {
      input.value = window.location.search
    })
  }

  // Every selected item, on this page or kept from another one. A Set, so a
  // row rendered twice (desktop + mobile) is reported once. (CONEK-6287)
  selectedValues() {
    return [...this.selection]
  }

  syncIds(container, values, name) {
    container.innerHTML = ""
    values.forEach((value) => {
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = name
      input.value = value
      container.appendChild(input)
    })
  }

  // Delete now routes to a server-rendered impact preview page
  // (bulk_confirm_destroy) instead of a native confirm() dialog, so the user
  // sees what each deletion affects before committing. (CONEK-637)
  // The form POSTs (ids[] travel in the body, no URL-length limit), so the only
  // case we guard here is an empty selection.
  confirmDestroy(event) {
    if (!this.matchingMode && this.selection.size === 0) {
      event.preventDefault()
    }
  }
}
